import { useMemo, useState } from 'react';
import styles from './ContactSettings.module.scss';
import Loader from '../../Molecules/Loader/Loader';
import DeletePopup from '../../Molecules/Custom/DeletePopup';
import Buttons from '../../Atoms/Buttons/Buttons';
import ContactSettingFormModal from '../../Molecules/ContactSettings/ContactSettingFormModal';
import {
  useFetchContactSettings,
  useCreateContactSetting,
  useUpdateContactSetting,
  useDeleteContactSetting,
} from '../../../Hooks/ContactSettings';
import { useToast } from '../../Molecules/Custom/CustomToaster/Toast';
import { toast } from 'react-toastify';

/**
 * الجهات التي يتم إرسال نسخة بالبريد إليها — the allowed-recipient list behind
 * the website's /academics/contact-us form.
 *
 * WHY THIS IS ITS OWN SCREEN. It started as a panel bolted to the top of the
 * contact-messages page, back when the only thing an editor could do was flip
 * one boolean against a hard-coded list of six departments. The backend now
 * serves full CRUD, so this is a collection an editor owns outright — adding,
 * renaming and removing rows — and a collection sharing a screen with an
 * unrelated inbox is two jobs fighting over one page. Messages are a log to
 * read; this is configuration to edit.
 *
 * THIS LIST IS THE SOURCE OF TWO THINGS
 *
 *  1. Which departments a visitor can pick on the website's contact form.
 *  2. Which of them get a copy of the message e-mailed to them.
 *
 * A message is ALWAYS stored and always appears in «رسائل التواصل» regardless
 * of the switch — the switch only decides whether a copy is also e-mailed. That
 * distinction is stated on the screen because getting it backwards is how a
 * department is turned off and then assumed to be unreachable.
 *
 * NO PAGINATION. `GET /api/ContactUs/Settings` returns the whole list, sorted
 * by title and including inactive rows. It is a handful of departments, not a
 * dataset, so there is nothing to page and nothing to filter — a search box
 * over ten rows is furniture.
 */
const ContactSettingsPage = () => {
  const customizedToast = useToast();

  const { data, isLoading, isError, error, refetch } = useFetchContactSettings();
  const { mutate: createSetting, isLoading: isCreating } =
    useCreateContactSetting();
  const { mutate: updateSetting, isLoading: isUpdating } =
    useUpdateContactSetting();
  const { mutate: deleteSetting, isLoading: isDeleting } =
    useDeleteContactSetting();

  // `null` = closed. `{}` = the add dialog. A row = the edit dialog.
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  // Which row's switch is mid-flight, so only that one is disabled rather than
  // the whole table.
  const [savingEmail, setSavingEmail] = useState(null);
  // Validation straight off a rejected save, placed under its own field by the
  // dialog. Cleared on every new attempt so a fixed error does not linger.
  const [formError, setFormError] = useState({ errors: null, message: '' });

  const rows = useMemo(() => data ?? [], [data]);

  const activeCount = rows.filter((row) => row?.isActive).length;
  const copyCount = rows.filter(
    (row) => row?.isActive && row?.isEmailSentRequired
  ).length;

  const closeForm = () => {
    setEditing(null);
    setFormError({ errors: null, message: '' });
  };

  /**
   * A failed save keeps the dialog open with the operator's values intact —
   * closing it would throw away what they typed over an error they can fix in
   * one keystroke, which for the duplicate-address case is the whole point.
   */
  const handleSaveError = (err) => {
    setFormError({
      errors: err?.errors ?? null,
      // Only show a banner for something the fields cannot carry themselves.
      message: err?.errors ? '' : (err?.message ?? 'تعذّر حفظ جهة التواصل'),
    });
  };

  const handleSubmit = (values) => {
    setFormError({ errors: null, message: '' });

    const isEdit = !!editing?.email;
    const onSuccess = () => {
      customizedToast.open(
        isEdit ? 'تم تحديث جهة التواصل بنجاح' : 'تمت إضافة جهة التواصل بنجاح'
      );
      closeForm();
      refetch();
    };

    if (isEdit) {
      updateSetting(values, { onSuccess, onError: handleSaveError });
    } else {
      createSetting(values, { onSuccess, onError: handleSaveError });
    }
  };

  /**
   * The copy switch saves on the spot rather than opening the dialog: it is the
   * one field an editor changes often, and per-row saving means two editors
   * changing two different departments cannot overwrite each other.
   *
   * There is no PATCH — the backend validates `title` as required on PUT — so
   * the whole entity goes back with just this field flipped.
   *
   * No optimistic update. `refetch` is the only thing that moves the switch, so
   * what is on screen is always what the server acknowledged; a switch that
   * moves and then springs back is worse than one that waits a moment.
   */
  const handleToggleCopy = (row) => {
    setSavingEmail(row.email);

    updateSetting(
      {
        originalEmail: row.email,
        email: row.email,
        title: row.title,
        // Carried through unchanged, same as `title` and `isActive` above —
        // `mobile` isn't optional server-side once the backend deploys it, so
        // a toggle-only PUT must not drop it even though this row's own value
        // came back from a GET that (today) never returns it.
        mobile: row.mobile,
        isEmailSentRequired: !row.isEmailSentRequired,
        isActive: row.isActive,
      },
      {
        onSuccess: () => {
          setSavingEmail(null);
          customizedToast.open('تم تحديث إعدادات الإرسال');
          refetch();
        },
        onError: (err) => {
          setSavingEmail(null);
          toast.error(err?.message ?? 'تعذّر تحديث إعدادات الإرسال');
        },
      }
    );
  };

  const handleDelete = () => {
    const email = pendingDelete?.email;
    if (!email) return;

    deleteSetting(email, {
      onSuccess: () => {
        customizedToast.open('تم حذف جهة التواصل بنجاح');
        setPendingDelete(null);
        refetch();
      },
      onError: (err) => {
        toast.error(err?.message ?? 'تعذّر حذف جهة التواصل');
        setPendingDelete(null);
      },
    });
  };

  return (
    <div className={styles['settings-page']}>
      <div className={styles['page-head']}>
        <div className={styles['page-heading']}>
          <h2 className={styles['page-title']}>
            الجهات التي يتم إرسال نسخة بالبريد إليها
          </h2>
          {!isLoading && !isError && rows.length > 0 && (
            <span className={styles['total-pill']}>
              {activeCount} مُفعّلة من {rows.length} · {copyCount} تستلم نسخة
            </span>
          )}
        </div>

        <Buttons
          text='إضافة جهة'
          rounded={true}
          color='rgba(13, 160, 110, 1)'
          // `type='submit'` only to get the atom's BARE button: its other
          // branch wraps the button in a react-router `<Link to={undefined}>`.
          // There is no form on this page for it to submit.
          type='submit'
          width={160}
          // The atom's class sets `flex: 1`, which in this flex header would
          // stretch the button across half the page regardless of `width`.
          style={{ flex: '0 0 auto' }}
          handleOnClick={() => {
            setFormError({ errors: null, message: '' });
            setEditing({});
          }}
        />
      </div>

      <p className={styles['page-hint']}>
        هذه القائمة تحدّد الجهات التي يمكن للزائر اختيارها في صفحة «تواصل معنا».
        عند تفعيل «إرسال نسخة» تُرسل نسخة من رسالة الزائر إلى بريد الجهة، وفي
        جميع الحالات تُحفظ الرسالة وتظهر في صفحة «رسائل التواصل».
      </p>

      <div className={styles['table-container']}>
        {isLoading ? (
          <Loader />
        ) : isError ? (
          <p className={styles.empty}>
            {error?.message ?? 'تعذّر تحميل الجهات. يرجى المحاولة مرة أخرى.'}
          </p>
        ) : rows.length === 0 ? (
          <p className={styles.empty}>
            لا توجد جهات مُعرّفة. أضف جهة لتظهر للزائر في صفحة «تواصل معنا».
          </p>
        ) : (
          <div className={styles['table-scroll']}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope='col'>اسم الجهة</th>
                  <th scope='col'>البريد الإلكتروني</th>
                  <th scope='col'>رقم الهاتف</th>
                  <th scope='col'>إرسال نسخة بالبريد</th>
                  <th scope='col'>الحالة</th>
                  <th scope='col'>إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const inputId = `contact-copy-${row.email}`;
                  const isSavingRow = savingEmail === row.email;

                  return (
                    <tr
                      key={row.email}
                      className={row?.isActive ? undefined : styles.inactive}
                    >
                      <td className={styles['title-cell']}>
                        {row?.title || '—'}
                      </td>
                      <td className={styles['email-cell']}>
                        {/* An address is a Latin run in an RTL table; without
                            its own direction the dots and the @ reorder. */}
                        <a className={styles.ltr} href={`mailto:${row.email}`}>
                          {row.email}
                        </a>
                      </td>
                      <td className={styles['email-cell']}>
                        {/* `row.mobile` will read as "—" for every row until
                            the backend deploys the field (tracked in
                            Hooks/ContactSettings.js) — the API does not return
                            it on GET yet, even for a row saved with one. */}
                        {row?.mobile ? (
                          <a className={styles.ltr} href={`tel:${row.mobile}`}>
                            {row.mobile}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <div className={styles['switch-cell']}>
                          <input
                            id={inputId}
                            type='checkbox'
                            className={styles.switch}
                            checked={!!row?.isEmailSentRequired}
                            disabled={isSavingRow}
                            onChange={() => handleToggleCopy(row)}
                            aria-label={`إرسال نسخة بالبريد إلى ${row.title || row.email}`}
                          />
                          {isSavingRow && (
                            <span className={styles['switch-hint']}>
                              جارٍ الحفظ…
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        {/* Text, not colour alone — WCAG 1.4.1. */}
                        <span
                          className={
                            row?.isActive ? styles.badge : styles['badge-muted']
                          }
                        >
                          {row?.isActive ? 'مُفعّلة' : 'غير مُفعّلة'}
                        </span>
                      </td>
                      <td>
                        <div className={styles['row-actions']}>
                          <button
                            type='button'
                            className={styles['link-button']}
                            onClick={() => {
                              setFormError({ errors: null, message: '' });
                              setEditing(row);
                            }}
                          >
                            تعديل
                          </button>
                          <button
                            type='button'
                            className={`${styles['link-button']} ${styles.danger}`}
                            onClick={() => setPendingDelete(row)}
                          >
                            حذف
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <ContactSettingFormModal
          // Remounts between rows, so the fields always start from the row
          // being opened rather than the one opened before it.
          key={editing.email ?? 'new'}
          setting={editing.email ? editing : null}
          isSaving={isCreating || isUpdating}
          serverErrors={formError.errors}
          serverMessage={formError.message}
          onSubmit={handleSubmit}
          onClose={closeForm}
        />
      )}

      {pendingDelete && (
        <DeletePopup
          title='الجهة'
          isLoading={isDeleting}
          // The delete is permanent on the server — there is no soft delete and
          // no undo — and an editor who only wants it off the website should be
          // told the cheaper option exists before they take the expensive one.
          description={`${pendingDelete.title || pendingDelete.email} — الحذف نهائي. إذا كنت تنوي إعادة الجهة لاحقًا، عطّلها من «تعديل» بدل حذفها.`}
          handleOnClose={() => setPendingDelete(null)}
          handleDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default ContactSettingsPage;
